import React, { useState } from 'react';
// Para los iconos, necesitas instalar lucide-react: npm install lucide-react
import { Activity, Pill, FileText, Monitor, Users, Lock, Menu, X } from 'lucide-react';
import { Link } from 'react-router-dom';

// Componente reutilizable para las tarjetas de los módulos/servicios
const FeatureCard = ({ icon: Icon, title, description, color }) => {
  const colorClasses = {
    blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
    green: { bg: 'bg-green-100', text: 'text-green-600' },
    purple: { bg: 'bg-purple-100', text: 'text-purple-600' },
    orange: { bg: 'bg-orange-100', text: 'text-orange-600' },
    teal: { bg: 'bg-teal-100', text: 'text-teal-600' },
    red: { bg: 'bg-red-100', text: 'text-red-600' },
  };
  const selectedColor = colorClasses[color] || colorClasses.blue;

  return (
    <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 transform hover:-translate-y-2">
      <div className={`flex items-center justify-center h-16 w-16 rounded-full ${selectedColor.bg} mb-6`}>
        <Icon className={selectedColor.text} size={32} />
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-3">{title}</h3>
      <p className="text-gray-600 leading-relaxed">{description}</p>
    </div>
  );
};

const LandingPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gray-50 font-sans text-gray-800">
      {/* --- Barra de Navegación --- */}
      <header className="bg-white/80 backdrop-blur-md sticky top-0 z-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-2">
              <Activity className="h-8 w-8 text-blue-600" />
              <span className="text-2xl font-bold text-gray-900">
                Tu<span className="text-blue-600">HIS</span>
              </span>
            </div>

            {/* Menú de Escritorio */}
            <div className="hidden md:flex items-center space-x-10">
              <nav className="flex items-center space-x-8">
                <a href="#features" className="text-gray-600 hover:text-blue-600 transition-colors font-medium">Módulos</a>
                <a href="#integration" className="text-gray-600 hover:text-blue-600 transition-colors font-medium">Integración</a>
                <a href="#contact" className="text-gray-600 hover:text-blue-600 transition-colors font-medium">Contacto</a>
              </nav>
              <div className="flex items-center space-x-4">
                <Link to="/login" className="text-blue-600 font-medium hover:text-blue-800 transition-colors">
                  Iniciar Sesión
                </Link>
                <a href="#demo" className="bg-gradient-to-r from-blue-600 to-blue-500 text-white px-5 py-2.5 rounded-lg font-semibold hover:from-blue-700 hover:to-blue-600 transition-all duration-300 shadow-md hover:shadow-lg">
                  Solicitar Demo
                </a>
              </div>
            </div>

            {/* Botón de Menú Móvil */}
            <div className="md:hidden">
              <button onClick={() => setIsMenuOpen(!isMenuOpen)} aria-label="Toggle menu">
                {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Menú Desplegable Móvil */}
        {isMenuOpen && (
          <div className="md:hidden bg-white border-t">
            <nav className="px-4 pt-2 pb-4 space-y-2">
              <a href="#features" className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-50">Módulos</a>
              <a href="#integration" className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-50">Integración</a>
              <a href="#contact" className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-50">Contacto</a>
              <div className="border-t my-2"></div>
              <Link to="/login" className="block px-3 py-2 rounded-md text-base font-medium text-blue-600 hover:bg-blue-50">Iniciar Sesión</Link>
              <a href="#demo" className="block text-center mt-2 px-3 py-3 rounded-md text-base font-medium text-white bg-blue-600 hover:bg-blue-700">Solicitar Demo</a>
            </nav>
          </div>
        )}
      </header>

      {/* --- Sección Principal (Hero) --- */}
      <main>
        <section className="relative bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="relative z-10 pb-8 bg-white sm:pb-16 md:pb-20 lg:max-w-2xl lg:w-full lg:pb-28 xl:pb-32">
              <main className="mt-10 mx-auto max-w-7xl px-4 sm:mt-12 sm:px-6 md:mt-16 lg:mt-20 lg:px-8 xl:mt-28">
                <div className="sm:text-center lg:text-left">
                  <h1 className="text-4xl tracking-tight font-extrabold text-gray-900 sm:text-5xl md:text-6xl">
                    <span className="block xl:inline">La Plataforma HIS para la</span>{' '}
                    <span className="block text-blue-600 xl:inline">Salud del Futuro</span>
                  </h1>
                  <p className="mt-3 text-base text-gray-500 sm:mt-5 sm:text-lg sm:max-w-xl sm:mx-auto md:mt-5 md:text-xl lg:mx-0">
                    Un sistema de información hospitalaria modular, seguro y conectado. Optimice sus flujos de trabajo, desde la admisión hasta el laboratorio, con una solución integral.
                  </p>
                  <div className="mt-5 sm:mt-8 sm:flex sm:justify-center lg:justify-start">
                    <div className="rounded-md shadow">
                      <a
                        href="#demo"
                        className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 md:py-4 md:text-lg md:px-10"
                      >
                        Solicitar Demo
                      </a>
                    </div>
                    <div className="mt-3 sm:mt-0 sm:ml-3">
                      <a
                        href="#features"
                        className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 md:py-4 md:text-lg md:px-10"
                      >
                        Ver Módulos
                      </a>
                    </div>
                  </div>
                </div>
              </main>
            </div>
          </div>
          <div className="lg:absolute lg:inset-y-0 lg:right-0 lg:w-1/2">
            <img
              className="h-56 w-full object-cover sm:h-72 md:h-96 lg:w-full lg:h-full"
              src="/images/Doc_tablet.jpg"
              alt="Doctor using a tablet"
            />
            <div className="absolute inset-0 bg-blue-600 opacity-10"></div>
          </div>
        </section>

        {/* --- Sección de Módulos (Features) --- */}
        <section id="features" className="py-24 bg-gradient-to-b from-white to-blue-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-base font-semibold text-blue-600 tracking-wide uppercase">Arquitectura Modular</h2>
              <p className="mt-2 text-3xl md:text-4xl font-extrabold text-gray-900 tracking-tight">
                Un Ecosistema Completo para su Institución
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <FeatureCard
                icon={Users}
                title="Gestión de Pacientes (MPI)"
                description="Registro centralizado, historial de admisiones y datos demográficos seguros para una visión 360° del paciente."
                color="blue"
              />
              <FeatureCard
                icon={FileText}
                title="Consulta Externa y EHR"
                description="Agenda de citas, historia clínica electrónica (SOAP) y generación de órdenes médicas de forma intuitiva."
                color="green"
              />
              <FeatureCard
                icon={Pill}
                title="Módulo de Farmacia"
                description="Gestión de inventario, dispensación de medicamentos y alertas de interacciones para máxima seguridad."
                color="purple"
              />
              <FeatureCard
                icon={Activity}
                title="Laboratorio (LIS)"
                description="Integración nativa con equipos y sistemas de laboratorio vía HL7 para la gestión de órdenes y resultados."
                color="orange"
              />
              <FeatureCard
                icon={Monitor}
                title="Imagenología (RIS/PACS)"
                description="Coordinación de estudios de imagen y acceso a reportes e imágenes DICOM directamente desde el expediente."
                color="teal"
              />
              <FeatureCard
                icon={Lock}
                title="Administración y Seguridad"
                description="Control de acceso basado en roles (RBAC), logs de auditoría y cumplimiento de normativas de privacidad."
                color="red"
              />
            </div>
          </div>
        </section>
      </main>

      {/* --- Pie de Página (Footer) --- */}
      <footer className="bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto py-12 px-6 text-center">
          <p className="text-gray-500">
            &copy; {new Date().getFullYear()} TuHIS. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
